import type { CampaignCouponTemplateRecord } from '@navio/nsdk';
import { z } from 'zod';
import { appliesToModeFromLists } from '../../appliesToMode';
import { discountBrandValuesFromIds, refineDiscountBrands } from '../../discountBrands';
import { DISCOUNT_KIND } from '../../discountKind';
import {
  defaultPartnerDiscountUsageFormValues,
  DISCOUNT_USAGE_KIND,
  partnerUsageFromTemplate
} from '../../discountTerms';
import { refinePromotionDealFields } from '../../promotionDealValidation';
import {
  bookingExclusionRowSchema,
  bookingExclusionRowsFromSources,
  refineBookingExclusions
} from '../bookingExclusion';

export const validationSchema = z
  .object({
    name: z.string(),
    description: z.string(),
    discountKind: z.enum([DISCOUNT_KIND.amount, DISCOUNT_KIND.percentage]),
    discountAmount: z.number().nullable(),
    discountPercentage: z.number().nullable(),
    appliesToMode: z.object({
      appliesToAllProducts: z.boolean(),
      filterByProducts: z.boolean(),
      filterByTags: z.boolean()
    }),
    salesItemIds: z.array(z.number()),
    salesItemTagIds: z.array(z.number()),
    limitToBrands: z.boolean(),
    applicableBrandIds: z.array(z.number()),
    usageKind: z.enum([DISCOUNT_USAGE_KIND.once, DISCOUNT_USAGE_KIND.limited, DISCOUNT_USAGE_KIND.forever]),
    maxUsesCount: z.number().nullable(),
    bookingExclusions: z.array(bookingExclusionRowSchema)
  })
  .superRefine(({ appliesToMode, ...value }, ctx) => refinePromotionDealFields({ ...value, ...appliesToMode }, ctx))
  .superRefine(refineDiscountBrands)
  .superRefine(refineBookingExclusions);

export type FormValues = z.input<typeof validationSchema>;

export const getDefaultValues = (template: CampaignCouponTemplateRecord | undefined): FormValues => {
  if (template == null) {
    return {
      name: '',
      description: '',
      discountKind: DISCOUNT_KIND.amount,
      discountAmount: null,
      discountPercentage: null,
      appliesToMode: { appliesToAllProducts: true, filterByProducts: false, filterByTags: false },
      salesItemIds: [],
      salesItemTagIds: [],
      ...discountBrandValuesFromIds([]),
      bookingExclusions: [],
      ...defaultPartnerDiscountUsageFormValues()
    };
  }
  const usage = partnerUsageFromTemplate(template);
  return {
    name: template.namePublic,
    description: template.descriptionPublic,
    discountKind: template.discount && template.discount.amount > 0 ? DISCOUNT_KIND.amount : DISCOUNT_KIND.percentage,
    discountAmount: template.discount?.amount ?? null,
    discountPercentage: template.discountPercentage ?? null,
    appliesToMode: appliesToModeFromLists({
      productCount: template.validForSalesItems.length,
      tagCount: template.validForSalesItemTags.length
    }),
    salesItemIds: template.validForSalesItems.map((item) => item.id),
    salesItemTagIds: template.validForSalesItemTags.map((tag) => tag.id),
    ...discountBrandValuesFromIds(template.validForBrands.map((brand) => brand.id)),
    bookingExclusions: bookingExclusionRowsFromSources(template.bookingExclusions),
    usageKind: usage.usageKind,
    maxUsesCount: usage.maxUsesCount
  };
};
